/**
 * Social media utilities
 */

/**
 * Social media icon SVG paths
 */
// Icon glyphs adopted from the Ligonier "20-*" icon set (the live site's
// footer). They share consistent glyph padding within their viewBox so every
// icon renders at a uniform visual size.
const socialIcons: Record<string, string> = {
  facebook:
    "M8.023 18 8 11H5V8h3V6c0-2.7 1.672-4 4.08-4 1.153 0 2.144.086 2.433.124v2.821h-1.67c-1.31 0-1.563.623-1.563 1.536V8H15l-1 3h-2.72v7z",
  instagram:
    "M14 2c1.994 0 3.882 1.828 3.995 3.813L18 6v8c0 1.994-1.828 3.882-3.813 3.995L14 18H6c-1.994 0-3.882-1.828-3.995-3.813L2 14V6c0-1.994 1.828-3.882 3.813-3.995L6 2zm0 2H6c-.893 0-1.903.97-1.994 1.872L4 6v8c0 .91.954 1.905 1.87 1.994L6 16h8c.893 0 1.903-.97 1.994-1.872L16 14V6c0-.893-.97-1.903-1.872-1.994zm-4 2c2.206 0 4 1.794 4 4s-1.794 4-4 4-4-1.794-4-4 1.794-4 4-4m0 2c-1.103 0-2 .897-2 2s.897 2 2 2 2-.897 2-2-.897-2-2-2m4.145-3.068a.96.96 0 1 1 0 1.92.96.96 0 0 1 0-1.92",
  twitter:
    "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z",
  tiktok:
    "M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z",
  youtube:
    "M17.8 6.8c-.2-1.3-.8-2.2-2.2-2.4C13.4 4 10 4 10 4s-3.4 0-5.6.4C3 4.6 2.3 5.5 2.2 6.8 2 8.1 2 10 2 10s0 1.9.2 3.2.8 2.2 2.2 2.4c2.2.4 5.6.4 5.6.4s3.4 0 5.6-.4c1.4-.3 2-1.1 2.2-2.4S18 10 18 10s0-1.9-.2-3.2M8 13V7l5 3z",
  linkedin:
    "M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z",
}

/**
 * Default SVG viewBox for social icons. Facebook / Instagram / YouTube are
 * authored in a 20x20 space; X, TikTok and LinkedIn use their native 24x24.
 */
const DEFAULT_SOCIAL_ICON_VIEWBOX = "0 0 20 20"

const socialIconViewBoxes: Record<string, string> = {
  twitter: "0 0 24 24",
  tiktok: "0 0 24 24",
  linkedin: "0 0 24 24",
}

/**
 * Get SVG path for social media icon
 */
export function getSocialIcon(type: string): string {
  return socialIcons[type] || ""
}

/**
 * Get the SVG viewBox to use for a social media icon
 */
export function getSocialIconViewBox(type: string): string {
  return socialIconViewBoxes[type] || DEFAULT_SOCIAL_ICON_VIEWBOX
}

/**
 * Validate social media platform
 */
export function isValidSocialPlatform(platform: string): boolean {
  return Object.keys(socialIcons).includes(platform)
}

/**
 * Get all available social platforms
 */
export function getSocialPlatforms(): string[] {
  return Object.keys(socialIcons)
}